import { Toast, updateWidgetContent, Widget, WidgetEditor } from 'jamespot-front-business';
import { JRCWidgetCheckListEntries, Utils } from 'jamespot-react-components';
import jApi, {
    CombinedWidgetContent,
    ensureWidgetCheckListType,
    jFileLittle,
    WidgetCheckListContent,
    WidgetCheckListType,
    WidgetDisplayMode,
    WidgetsName,
} from 'jamespot-user-api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { shallowEqual } from 'react-redux';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { commonComponentsRegistry } from '../../../registry/CommonRegistry';
import { RTProvider } from '../../RTProvider.component';
import { useWidgetRT } from '../hooks';
import { WidgetFooterView } from './components/WidgetFooterView';
import { WidgetInlineInputText } from './components/WidgetInlineInputText';
import { WidgetIsPending } from './components/WidgetIsPending';

const WidgetCheckList = commonComponentsRegistry.getLazyComponent('WidgetCheckList');
const WidgetCheckListEditor = commonComponentsRegistry.getLazyComponent('WidgetCheckListEditor');
const JRCFileViewer = commonComponentsRegistry.getLazyComponent('FileViewer');

export const CSSRowLinkWrapper = styled.div<{ border?: boolean }>`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    border: ${(props) => (props.border ? `1px solid ${props.theme.color.grey2}` : 'none')};
    min-height: 24px;
    border-radius: 4px;
    padding: 4px 8px;
`;

const CommentContainer = styled.div`
    width: 100%;
    display: flex;
    gap: 14px;
    padding: 16px;
`;

export const CSSWidgetCheckListWrapper = styled.div`
    position: relative;
`;

type JRCWidgetCheckListWrapperProps = {
    widget: WidgetCheckListType;
    inplace: boolean;
    mode?: WidgetDisplayMode;
    onWidgetUpdate?: () => void;
};

export const JRCWidgetCheckListWrapper = ({
    widget,
    inplace,
    mode,
    onWidgetUpdate,
}: JRCWidgetCheckListWrapperProps) => {
    const intl = useIntl();
    const { uniqid, content: widgetContent } = widget;
    const dispatch = useAppDispatch();
    const [token, setToken] = useState<string>();
    const [tokenLoading, setTokenLoading] = useState(false);

    const [entries, setEntries] = useState<JRCWidgetCheckListEntries | undefined>();
    const [isPending, setIsPending] = useState(false);

    const tokenFromStore = useAppSelector(Widget.selectors.selectToken, shallowEqual);

    const uri = useAppSelector((state) => Widget.selectors.selectWidgetObjectUri(state, widget.uniqid), shallowEqual);

    const widgetObjectRights = useAppSelector(
        (state) => Widget.selectors.selectWidgetObjectRights(state, uniqid),
        shallowEqual,
    );

    const allRt = useAppSelector(
        (state) => Widget.selectors.selectAllWidgetRTObject(state, widget.uniqid),
        shallowEqual,
    );

    const canEditCollaboration = useMemo(() => {
        return widgetObjectRights && widgetObjectRights.update;
    }, [widgetObjectRights]);

    const canAdministrate = useMemo(() => {
        return widgetObjectRights && widgetObjectRights.administrate;
    }, [widgetObjectRights]);

    const onError = useCallback(
        (message?: string) => {
            dispatch(Toast.actions.error({ label: message ?? intl.formatMessage({ id: 'GLOBAL_Error' }) }));
        },
        [dispatch, intl],
    );

    const fetchToken = useCallback(() => {
        if (!token && !tokenLoading) {
            setTokenLoading(true);
            jApi.network
                .token()
                .then((res) => {
                    setToken(res.result);
                    setTokenLoading(false);

                    // set comment token to our widget slice to be able to upload image inside our widgets
                    dispatch(Widget.slice.actions.setToken({ token: res.result }));
                })
                .catch((e) => {
                    onError(e.errorMsg);
                });
        }
    }, [onError, token, tokenLoading, dispatch]);

    if (!tokenFromStore) {
        fetchToken();
    } else if (!token) {
        setToken(tokenFromStore);
    }

    const onChange = useCallback(
        (uniqid: string, content: WidgetCheckListContent) => {
            dispatch(updateWidgetContent(uniqid, content));
        },
        [dispatch],
    );

    const onChangeWrapper = (content: { uniqid: string; title?: string }) => {
        dispatch(Widget.slice.actions.updateWidgetWrapper({ ...content }));
    };

    const handleCheckItem = (id: string, index: number) => {
        if (!uri) return;
        if (isPending) return;
        setIsPending(true);
        jApi.widget
            .checkListRespond({
                uri,
                uniqid: id,
                index: index,
            })
            .then(() => {
                if (onWidgetUpdate) onWidgetUpdate();
            })
            .catch((err) => {
                onError(err.message);
                setIsPending(false);
            });
    };

    const handleReloadWidget = useCallback(() => {
        if (!uri) return;
        setIsPending(true);
        jApi.widget
            .getWidget<WidgetCheckListType>({ uri, uniqid })
            .then((response) => {
                if (ensureWidgetCheckListType(response.result)) {
                    onChange(uniqid, response.result.content);
                }
            })
            .catch((err) => {
                return !err.aborted && onError;
            })
            .finally(() => setIsPending(false));
    }, [uniqid, onError, uri, onChange]);

    const openEditorPanel = (token: string) => {
        dispatch(WidgetEditor.slice.actions.flushAllEditor());
        dispatch(Widget.slice.actions.setToken({ token }));
        dispatch(
            WidgetEditor.slice.actions.registerEditor({
                uniqid: widget.uniqid,
                name: WidgetsName.CheckList,
                mode,
            }),
        );
    };

    useEffect(() => {
        if (!token && mode === 'edit') {
            jApi.network.token().then((response) => {
                dispatch(Widget.slice.actions.setToken({ token: response.result }));
            });
        }
    }, [mode, token, dispatch]);

    useEffect(() => {
        const finalEntries: JRCWidgetCheckListEntries | undefined = widget.content.arr?.map((item) => ({
            text: item.title ?? '',
            file: item.file ?? undefined,
            checked: item.value ?? false,
            ...(item.links && {
                links: item.links,
            }),
            ...(item.user && { user: item.user ?? undefined }),
            ...(item.id && { id: item.id, mimetype: 'image/png' }),
        }));

        setEntries(finalEntries);
    }, [widget.content.arr, widgetContent]);

    useEffect(() => {
        if (!Array.isArray(allRt)) return;
        const lastRt = allRt.at(-1);
        if (!lastRt) return;
        if (!uri) return;

        if (lastRt.object.uri === uri && lastRt.uniqid === widget.uniqid) {
            if (lastRt.fn === 'check-list-response' || lastRt.fn === 'widget-update') {
                handleReloadWidget();
            }
        }
    }, [allRt, widget.uniqid, uri, onError, dispatch, handleReloadWidget]);

    const handleCollabAddEntry = (label: string) => {
        if (!widget.content.arr || !uri) return;

        const contentToUpdate = {
            ...widget.content,
            arr: [...widget.content.arr, { title: label, value: false }],
        };

        jApi.widget
            .updateWidget<CombinedWidgetContent>({
                uri,
                uniqid: widget.uniqid,
                content: contentToUpdate as CombinedWidgetContent,
                v2: true,
            })
            .then(() => onChange(widget.uniqid, contentToUpdate))
            .catch((err) => onError(err.errorMsg));
    };

    const files = useMemo(() => {
        if (!Array.isArray(widgetContent.arr)) return [];

        const fs: jFileLittle[] = [];
        widgetContent.arr.forEach((el) => {
            if (el.file) {
                fs.push(el.file);
            }
        });

        return fs;
    }, [widgetContent]);

    if (mode === 'edit') {
        if (inplace) {
            return (
                <CommentContainer>
                    <WidgetCheckListEditor
                        token={token}
                        uniqid={uniqid}
                        content={{
                            ...widgetContent,
                            arr: widgetContent.arr?.map((w) => ({
                                ...w,
                                title: Utils.html.htmlDecodeSpecialChars(w.title),
                            })),
                        }}
                        inplace={inplace}
                        onChangeContent={onChange}
                        onChangeWrapper={onChangeWrapper}
                        handleChange={(content) =>
                            dispatch(updateWidgetContent<WidgetCheckListContent>(widget.uniqid, content))
                        }
                    />
                </CommentContainer>
            );
        }
    }

    return (
        <RTProvider
            useRT={() => {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                useWidgetRT(widget.uniqid);
            }}>
            {entries && (
                <JRCFileViewer files={files} openWithOptions={[]}>
                    {(open) => {
                        return (
                            <CSSWidgetCheckListWrapper key={widget.uniqid}>
                                <WidgetCheckList
                                    limit={10}
                                    initialEntries={entries}
                                    onFileClick={(entry) => entry.id && open(entry.id)}
                                    onItemCheckListChange={(index) => handleCheckItem(widget.uniqid, index)}
                                />
                                {widgetContent.edit && (
                                    <WidgetInlineInputText
                                        handleClick={handleCollabAddEntry}
                                        placeholder={intl.formatMessage({
                                            id: 'COMMENT_WIDGET_CHECKLIST_Add_New_Task',
                                        })}
                                    />
                                )}
                                {canAdministrate && token && (
                                    <WidgetFooterView
                                        uniqid={widget.uniqid}
                                        name={widget.name}
                                        collaborationRights={{
                                            update: !!(widgetContent.edit || canEditCollaboration),
                                        }}
                                        mode={mode}
                                        handleOpenEditor={() => {
                                            if (!token) {
                                                setIsPending(true);
                                                jApi.network
                                                    .token()
                                                    .then((response) => {
                                                        dispatch(
                                                            Widget.slice.actions.setToken({ token: response.result }),
                                                        );
                                                        openEditorPanel(response.result);
                                                    })
                                                    .finally(() => setIsPending(false));
                                            } else {
                                                openEditorPanel(token);
                                            }
                                        }}
                                    />
                                )}
                                {isPending && <WidgetIsPending />}
                            </CSSWidgetCheckListWrapper>
                        );
                    }}
                </JRCFileViewer>
            )}
        </RTProvider>
    );
};
